import React, {useState} from "react";
import {Timeline, Divider, Button, Modal, Tag} from "antd";
import moment from "moment";
// import "moment/locale/de";
import {
    InfoCircleOutlined,
    PlusCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    MailOutlined,
    SwapOutlined,
    FileTextOutlined,
    UserOutlined,
    LinkOutlined,
    RollbackOutlined,
    StopOutlined,
} from "@ant-design/icons";
import {BookingHistoryByIdWithBookingInfoQuery, BookingHistoryAction} from "../../generated/graphql";
import css from "./BookingHistory.less";
// Importiere die Hilfsfunktionen
import {renderUpdateDate, renderUser, formatHistoryDate} from "./bookingHistoryRenderHelpers";
import BookingHistoryDetails from "./BookingHistoryDetails";
import {parseDescription} from "./bookingHistoryDescription";
import TagComponent from "../../views/Tag/Tag";

type HistoryItemProps = {
    item: BookingHistoryByIdWithBookingInfoQuery["bookingHistoryByIdWithBookingInfo"][number];
};

// Icon und Farbe für Aktionstypen
type ActionConfig = {
    icon: React.ReactNode;
    color: string;
};

const getActionConfig = (action: BookingHistoryAction): ActionConfig => {
    switch (action) {
        // Erstellen
        case BookingHistoryAction.BookingCreate:
        case BookingHistoryAction.WaitlistCreate:
        case BookingHistoryAction.PlaceReservationCreate:
        case BookingHistoryAction.PlaceReservationCreateByMaxAttendeesIncrease:
        case BookingHistoryAction.Created:
            return {icon: <PlusCircleOutlined />, color: "green"};

        // Löschen
        case BookingHistoryAction.BookingDelete:
        case BookingHistoryAction.BookingDeleteTransfer:
        case BookingHistoryAction.BookingOrphanDelete:
        case BookingHistoryAction.WaitlistDelete:
        case BookingHistoryAction.WaitlistDeleteTransfer:
        case BookingHistoryAction.PlaceReservationDelete:
            return {icon: <DeleteOutlined />, color: "red"};

        // Transfer/Verschieben
        case BookingHistoryAction.BookingCreateTransfer:
        case BookingHistoryAction.BookingCreateTransferMove:
        case BookingHistoryAction.WaitlistCreateTransfer:
        case BookingHistoryAction.WaitlistCreateTransferMove:
            return {icon: <SwapOutlined />, color: "orange"};

        // Kopieren
        case BookingHistoryAction.BookingCreateTransferCopy:
        case BookingHistoryAction.WaitlistCreateTransferCopy:
            return {icon: <FileTextOutlined />, color: "blue"};

        // Umwandlung
        case BookingHistoryAction.BookingToWaitlist:
        case BookingHistoryAction.WaitlistToBooking:
        case BookingHistoryAction.WaitlistToBookingByMaxAttendeesIncrease:
        case BookingHistoryAction.PlaceReservationToBooking:
            return {icon: <SwapOutlined />, color: "purple"};

        // Abrechnung/Bezahlt
        case BookingHistoryAction.BookingSettle:
        case BookingHistoryAction.ScheduledPaymentPaid:
            return {icon: <CheckCircleOutlined />, color: "green"};

        // Storniert
        case BookingHistoryAction.BookingCancle:
        case BookingHistoryAction.Canceled:
        case BookingHistoryAction.ScheduledPaymentUnpaid:
            return {icon: <CloseCircleOutlined />, color: "red"};

        // Preis/Zahlung/Rabatt
        case BookingHistoryAction.PriceChanged:
        case BookingHistoryAction.PaymentChanged:
        case BookingHistoryAction.ScheduledPaymentCreated:
        case BookingHistoryAction.ScheduledPaymentUpdated:
        case BookingHistoryAction.ScheduledPaymentDeleted:
        case BookingHistoryAction.PaymentMethodChanged:
        case BookingHistoryAction.DiscountChanged:
        case BookingHistoryAction.BookingCorrected:
            return {icon: <DollarOutlined />, color: "gold"};

        // Rechnung
        case BookingHistoryAction.CorrectionInvoice:
        case BookingHistoryAction.SendInvoice:
            return {icon: <FileTextOutlined />, color: "cyan"};

        // Email
        case BookingHistoryAction.SendBookingConfirmation:
        case BookingHistoryAction.SendWaitlistConfirmation:
        case BookingHistoryAction.EmailSent:
            return {icon: <MailOutlined />, color: "blue"};

        // Zahlungslink-Versand (Mollie)
        case BookingHistoryAction.PaymentLinkSent:
            return {icon: <LinkOutlined />, color: "gold"};

        // Zahlungslink entwertet (Mollie) — gleiche Farb-Familie wie PaymentLinkSent.
        case BookingHistoryAction.MolliePaymentLinkVoided:
            return {icon: <StopOutlined />, color: "gold"};

        // Erstattung (Mollie) — "Geld zurück": RollbackOutlined in Orange.
        case BookingHistoryAction.RefundInitiated:
        case BookingHistoryAction.RefundCompleted:
            return {icon: <RollbackOutlined />, color: "orange"};

        // Anwesenheit/Lektionen
        case BookingHistoryAction.AttendanceChanged:
            return {icon: <UserOutlined />, color: "geekblue"};
        case BookingHistoryAction.LessonAdded:
            return {icon: <PlusCircleOutlined />, color: "green"};
        case BookingHistoryAction.LessonRemoved:
            return {icon: <DeleteOutlined />, color: "red"};

        // Status/Update
        case BookingHistoryAction.StatusChanged:
        case BookingHistoryAction.Updated:
            return {icon: <EditOutlined />, color: "orange"};

        // Unbekannt/Custom
        case BookingHistoryAction.Custom:
        case BookingHistoryAction.Unknown:
        default:
            return {icon: <InfoCircleOutlined />, color: "default"};
    }
};

/**
 * Aktions-Prioritaet fuer die Gruppen-Header-Auswahl.
 *
 * Wenn mehrere History-Eintraege dieselbe `correlationId` teilen (also
 * aus einer einzelnen User-Aktion stammen), wird der Eintrag mit dem
 * niedrigsten Wert als "primaer" angezeigt, alle weiteren als
 * Folge-Eintraege darunter eingerueckt.
 *
 * Variante (a) gem. Designentscheidung: Strukturelle Aenderungen am
 * Buchungsumfang (LessonAdded/Removed) und Stornierungen rangieren ueber
 * Preis-/Zahlungs-Folgewirkungen, weil sie den eigentlichen Trigger
 * darstellen — "Posten aktualisiert" ist eine Konsequenz aus
 * "Kurseinheit hinzugefuegt", nicht umgekehrt.
 */
const getActionPriority = (action: BookingHistoryAction): number => {
    switch (action) {
        // Strukturelle Aenderungen / Lebenszyklus
        case BookingHistoryAction.BookingCreate:
        case BookingHistoryAction.BookingDelete:
        case BookingHistoryAction.BookingCancle:
        case BookingHistoryAction.Canceled:
        case BookingHistoryAction.BookingSettle:
        case BookingHistoryAction.LessonAdded:
        case BookingHistoryAction.LessonRemoved:
        case BookingHistoryAction.BookingToWaitlist:
        case BookingHistoryAction.WaitlistToBooking:
        case BookingHistoryAction.PlaceReservationToBooking:
            return 10;

        // Umbuchung (Engine) ist der Auslöser: Abrechnung, Storno und Zahlungen derselben
        // Korrelation sind ihre Folge — auch vor den übrigen Lebenszyklus-Aktionen.
        case BookingHistoryAction.BookingCreateTransferMove:
        case BookingHistoryAction.WaitlistCreateTransferMove:
        case BookingHistoryAction.BookingDeleteTransfer:
        case BookingHistoryAction.WaitlistDeleteTransfer:
            return 5;

        // Bewusste Konfigurations-Aenderungen am Booking
        case BookingHistoryAction.DiscountChanged:
        case BookingHistoryAction.BookingCorrected:
        case BookingHistoryAction.PaymentMethodChanged:
        case BookingHistoryAction.StatusChanged:
            return 20;

        // Preis-Aenderungen (folgen oft aus den oben genannten)
        case BookingHistoryAction.PriceChanged:
            return 30;

        // Zahlungsplan-Folgewirkungen
        case BookingHistoryAction.ScheduledPaymentCreated:
        case BookingHistoryAction.ScheduledPaymentUpdated:
        case BookingHistoryAction.ScheduledPaymentDeleted:
        case BookingHistoryAction.PaymentChanged:
            return 40;

        // Statusmarker (bezahlt/unbezahlt) + Erstattungs-Events (Zahlungs-Niveau)
        case BookingHistoryAction.ScheduledPaymentPaid:
        case BookingHistoryAction.ScheduledPaymentUnpaid:
        case BookingHistoryAction.AttendanceChanged:
        case BookingHistoryAction.RefundInitiated:
        case BookingHistoryAction.RefundCompleted:
            return 50;

        // Generische Updates
        case BookingHistoryAction.Updated:
            return 60;

        default:
            return 100;
    }
};

/**
 * Manche Backend-Events erzeugen redundante Description-Texte ohne
 * Aussagekraft, weil ein anderes Code-Pfad bereits den eigentlichen Effekt
 * geloggt hat. Konkret betrifft das Lesson-Add/Remove-Eintraege mit Count
 * 0 (preexisting Bug in lessonBooking.service.ts:1520, am 2026-05-05
 * gefixt — Filter bleibt fuer Bestandsdaten in der DB).
 *
 * Bewusst eng definiert: andere `…: 0`-Patterns (z.B. Geldbetraege,
 * Posten-Counts in legitimen Edge-Cases) bleiben sichtbar.
 */
const isNoOpLessonChangeDescription = (description?: string | null): boolean => {
    if (!description) {
        return false;
    }

    return /^Kurseinheiten\s+(entfernt|hinzugefügt|reduziert):\s*0\b/.test(description);
};

/**
 * Wenn mehrere Eintraege denselben Prioritaetswert haben, gewinnt der
 * informativere (Description ohne "...:0"), sonst zeitlich frueheste.
 */
const pickPrimaryItem = <T extends {action: BookingHistoryAction; createDate?: Date | string | null; description?: string | null}>(items: ReadonlyArray<T>): T => {
    return [...items].sort((a, b) => {
        const priorityDiff = getActionPriority(a.action) - getActionPriority(b.action);

        if (priorityDiff !== 0) {
            return priorityDiff;
        }
        // Tie-Breaker 1: "Kurseinheiten ...: 0"-Eintraege haben keine
        // Aussagekraft (preexisting Backend-Bug, jetzt gefixt).
        const aZero = isNoOpLessonChangeDescription(a.description) ? 1 : 0;
        const bZero = isNoOpLessonChangeDescription(b.description) ? 1 : 0;

        if (aZero !== bZero) {
            return aZero - bZero;
        }
        // Tie-Breaker 2: chronologisch frueheste — typischerweise der
        // semantische Trigger der Aktion.
        const aTime = a.createDate ? new Date(a.createDate).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.createDate ? new Date(b.createDate).getTime() : Number.POSITIVE_INFINITY;

        return aTime - bTime;
    })[0];
};

// HistoryItem-Komponente aus BookingHistoryDrawer extrahiert
const HistoryItem: React.FC<HistoryItemProps> = ({item}) => {
    const [open, setOpen] = useState(false);

    // Eintraege ohne Benutzer (Mollie-Webhook, Batch) tragen den Urheber nur im
    // gespeicherten userName ("System (Mollie-Webhook)") — vorher stand dort "N/A".
    const getUserInfo = (item: BookingHistoryByIdWithBookingInfoQuery["bookingHistoryByIdWithBookingInfo"][number]) => {
        const createdBy = item.createdBy;
        const userInfo = createdBy?.instructorDetails ? `${createdBy.instructorDetails.firstname} ${createdBy.instructorDetails.lastname}` : createdBy?.email ?? item.userName ?? "N/A";

        return (
            <TagComponent color="cyan" className={css.subSubLineItem}>
                {userInfo}
            </TagComponent>
        );
    };

    const actionConfig = getActionConfig(item.action);

    // Farbzuordnung für Timeline-Dot (Ant Design Farben)
    const getDotStyle = (color: string) => {
        const colorMap: Record<string, {bg: string; border: string; icon: string}> = {
            green: {bg: "#f6ffed", border: "#52c41a", icon: "#389e0d"},
            red: {bg: "#fff1f0", border: "#ff4d4f", icon: "#cf1322"},
            orange: {bg: "#fff7e6", border: "#fa8c16", icon: "#d46b08"},
            blue: {bg: "#e6f7ff", border: "#1890ff", icon: "#0958d9"},
            purple: {bg: "#f9f0ff", border: "#722ed1", icon: "#531dab"},
            gold: {bg: "#fffbe6", border: "#faad14", icon: "#d48806"},
            cyan: {bg: "#e6fffb", border: "#13c2c2", icon: "#08979c"},
            geekblue: {bg: "#f0f5ff", border: "#2f54eb", icon: "#1d39c4"},
            default: {bg: "#fafafa", border: "#d9d9d9", icon: "#595959"},
        };

        return colorMap[color] ?? colorMap.default;
    };

    const dotStyle = getDotStyle(actionConfig.color);

    const DetailsModal = () => {
        if (!open) {
            return (
                <Button type="link" size="small" onClick={() => setOpen(!open)} style={{padding: 0}} className={css.subSubLineItem}>
                    <InfoCircleOutlined />
                </Button>
            );
        }

        // Bug 2: Reinen String-Helper nutzen statt
        // `renderUpdateDate(...).props.children` - das war ein React-Children-
        // Array `["", "28.04.2026 13:16"]`, dessen Array.toString() ein
        // fuehrendes Komma erzeugte ("- ,28.04.2026 13:16").
        const updateDate = formatHistoryDate(item.createDate);
        // Suffix " (durch Kursanpassung)" aus dem Modal-Titel entfernen -
        // wir zeigen ihn an anderer Stelle als Tag bzw. als Alert.
        const cleanTitleDescription = splitDescription(parseDescription(item.description).cleanDescription).title;
        const modalTitle = updateDate
            ? `${cleanTitleDescription} - ${updateDate}`
            : cleanTitleDescription;

        return (
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                title={modalTitle}
                width={1000}
                footer={null}
            >
                <BookingHistoryDetails item={item} />
            </Modal>
        );
    };

    return (
        <Timeline.Item
            key={item.id}
            className={css.historyItem}
            dot={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: dotStyle.bg,
                        border: `2px solid ${dotStyle.border}`,
                    }}
                >
                    <span style={{color: dotStyle.icon, fontSize: "14px", display: "flex", alignItems: "center"}}>{actionConfig.icon}</span>
                </div>
            }
        >
            <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap"}}>
                {(() => {
                    // parseDescription einmal je Item ausfuehren - der Suffix
                    // " (durch Kursanpassung)" wird aus dem Action-Tag entfernt
                    // und stattdessen als separater purple-Tag dargestellt.
                    const parsed = parseDescription(item.description);
                    const {title, lines} = splitDescription(parsed.cleanDescription || item.description || "");

                    return (
                        <>
                            <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0, fontSize: "0.95em", whiteSpace: "normal"}}>
                                {title}
                            </Tag>
                            {parsed.isCourseEdit && (
                                <Tag color="purple" style={{margin: 0, fontSize: "0.85em"}}>
                                    {"Durch Kursanpassung"}
                                </Tag>
                            )}
                            <DescriptionLines lines={lines} />
                        </>
                    );
                })()}
            </div>
            <div className={css.entryMedium + " " + css.grey}>{renderUpdateDate(item.createDate, true)}</div>
            <div className={css.entrySmall}>{renderUser(item.user)}</div>
            <div className={css.subSubLine}>
                <div className={css.userInfo}>{getUserInfo(item)}</div>
                <DetailsModal />
            </div>
        </Timeline.Item>
    );
};

/**
 * Mehrzeilige Descriptions (z. B. die Umbuchungs-Zusammenfassung der Engine): die erste
 * Zeile ist der Titel fürs Tag, alle weiteren werden als Textzeilen darunter gezeigt.
 * Das Tag selbst darf umbrechen (`whiteSpace: normal`), damit auch lange einzeilige
 * Beschreibungen wie „Umgebucht nach Kurs …" nicht rechts abgeschnitten werden.
 */
const splitDescription = (description: string): {title: string; lines: Array<string>} => {
    const [title, ...lines] = description.split("\n").map((line) => line.trim()).filter((line) => line.length > 0);

    return {title: title ?? "", lines};
};

const DescriptionLines: React.FC<{lines: Array<string>}> = ({lines}) => lines.length === 0 ? null : (
    <div style={{fontSize: "0.9em", lineHeight: 1.4, whiteSpace: "pre-line", wordBreak: "break-word", flexBasis: "100%"}}>
        {lines.join("\n")}
    </div>
);

type HistoryItemType = BookingHistoryByIdWithBookingInfoQuery["bookingHistoryByIdWithBookingInfo"][number];

/**
 * Rendert mehrere History-Eintraege derselben User-Aktion (gleiche
 * `correlationId`) als zusammengehoerigen Block: das nach Action-Prioritaet
 * "primaere" Event als regulaeres `<HistoryItem>` an oberster Stelle, die
 * Folge-Events darunter eingerueckt mit kompakterer Darstellung. Jedes
 * Sub-Item behaelt seinen eigenen Details-Modal — der Nutzer kann die
 * granularen Diffs (z.B. Posten-Tabelle) weiterhin pro Folge-Effekt sehen.
 */
const HistoryGroup: React.FC<{items: ReadonlyArray<HistoryItemType>}> = ({items}) => {
    if (items.length === 1) {
        // Auch alleinstehende Bestandsdaten-Eintraege mit "Kurseinheiten …: 0"
        // verstecken (preexisting Backend-Bug). Bei korrelierten Gruppen wird
        // der Eintrag automatisch nicht primaer und in `secondary` gefiltert.
        if (isNoOpLessonChangeDescription(items[0].description)) {
            return null;
        }

        return <HistoryItem item={items[0]} />;
    }

    const primary = pickPrimaryItem(items);
    // Folge-Ereignisse mit "Kurseinheiten …: 0"-Descriptions werden ausgeblendet
    // (preexisting Backend-Bug, am Ursprung gefixt; Filter bleibt fuer
    // Bestandsdaten in der DB).
    const secondary = items.filter(
        (it) => it.id !== primary.id && !isNoOpLessonChangeDescription(it.description),
    );

    return (
        <>
            <HistoryItem item={primary} />
            {secondary.length > 0 && (
                <Timeline.Item
                    key={`${primary.id}-followups`}
                    className={css.historyItem}
                    dot={<span style={{display: "inline-block", width: 0, height: 0}} />}
                >
                    <div
                        style={{
                            marginLeft: 12,
                            paddingLeft: 12,
                            borderLeft: "2px dashed #d9d9d9",
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                        }}
                    >
                        <div style={{fontSize: "0.8em", color: "#999", textTransform: "uppercase", letterSpacing: 0.5}}>
                            {secondary.length === 1 ? "Folge-Ereignis" : `Folge-Ereignisse (${secondary.length})`}
                        </div>
                        {secondary.map((item) => (
                            <FollowUpItem key={item.id} item={item} />
                        ))}
                    </div>
                </Timeline.Item>
            )}
        </>
    );
};

/**
 * Kompakte Darstellung eines Folge-Events: ein Tag mit Action-Icon +
 * Description + Details-Modal-Button. Datum/User werden weggelassen,
 * weil sie identisch zum primaeren Event sind.
 */
const FollowUpItem: React.FC<{item: HistoryItemType}> = ({item}) => {
    const [open, setOpen] = useState(false);
    const actionConfig = getActionConfig(item.action);
    const parsed = parseDescription(item.description);
    const {title, lines} = splitDescription(parsed.cleanDescription || item.description || "");
    const updateDate = formatHistoryDate(item.createDate);
    const modalTitle = updateDate
        ? `${title} - ${updateDate}`
        : title;

    return (
        <div style={{display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap"}}>
            <Tag icon={actionConfig.icon} color={actionConfig.color} style={{margin: 0, fontSize: "0.85em", whiteSpace: "normal"}}>
                {title}
            </Tag>
            <Button
                type="link"
                size="small"
                onClick={() => setOpen(true)}
                style={{padding: 0}}
                aria-label="Details anzeigen"
            >
                <InfoCircleOutlined />
            </Button>
            <DescriptionLines lines={lines} />
            {open && (
                <Modal
                    open={open}
                    onCancel={() => setOpen(false)}
                    title={modalTitle}
                    width={1000}
                    footer={null}
                >
                    <BookingHistoryDetails item={item} />
                </Modal>
            )}
        </div>
    );
};

/**
 * Gruppiert eine Item-Liste nach `correlationId`. Items ohne ID landen
 * jeweils in einer eigenen Singleton-Gruppe (Backwards-compat zu
 * Bestandsdaten ohne Korrelations-ID).
 *
 * Reihenfolge der Gruppen ergibt sich aus dem ersten Vorkommen jeder
 * correlationId in der Eingabeliste — die Aufrufer sortieren nach
 * createDate vor.
 */
const groupByCorrelation = (items: ReadonlyArray<HistoryItemType>): Array<Array<HistoryItemType>> => {
    const groups: Array<Array<HistoryItemType>> = [];
    const indexByKey = new Map<string, number>();

    items.forEach((item) => {
        const key = item.correlationId ?? `__solo:${item.id}`;
        const existingIndex = indexByKey.get(key);

        if (existingIndex !== undefined) {
            groups[existingIndex].push(item);
        } else {
            indexByKey.set(key, groups.length);
            groups.push([item]);
        }
    });

    return groups;
};

type BookingHistoryContentProps = {
    history: BookingHistoryByIdWithBookingInfoQuery["bookingHistoryByIdWithBookingInfo"];
};

const BookingHistoryContent: React.FC<BookingHistoryContentProps> = ({history}) => {
    // Deutsche Lokalisierung aktivieren
    moment.locale("de");

    if (history.length === 0) {
        return <div>{"Keine Historie gefunden"}</div>;
    }

    // Gruppierung der Einträge nach Zeitperioden
    const today = moment().startOf("day");
    const groupedItems = {
        days: {} as Record<string, typeof history>,
        weeks: {} as Record<string, typeof history>,
        months: {} as Record<string, typeof history>,
    };

    // Sortiere Einträge in entsprechende Zeitperioden
    history.forEach((item) => {
        const itemDate = moment(item.createDate);
        const diffDays = today.diff(itemDate, "days");

        if (diffDays < 7) {
            // Letzten 7 Tage - nach Tagen gruppieren
            const dayKey = itemDate.format("DD.MM.YYYY");

            groupedItems.days[dayKey] = [...(groupedItems.days[dayKey] || []), item];
        } else if (diffDays < 30) {
            // Älter als 7 Tage aber neuer als 1 Monat - nach Wochen gruppieren
            const weekKey = `Woche ${itemDate.format("WW, YYYY")}`;

            groupedItems.weeks[weekKey] = [...(groupedItems.weeks[weekKey] || []), item];
        } else {
            // Älter als 1 Monat - nach Monaten gruppieren
            const monthKey = itemDate.format("MMMM YYYY");

            groupedItems.months[monthKey] = [...(groupedItems.months[monthKey] || []), item];
        }
    });

    return (
        <div className={css.timelineContainer}>
            {/* Einträge der letzten 7 Tage */}
            {Object.keys(groupedItems.days).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Letzte 7 Tage"}
                    </Divider>
                    {Object.entries(groupedItems.days).map(([day, items]) => (
                        <div key={day} className={css.timelineGroup}>
                            <div className={css.timelineGroupHeader}>{day}</div>
                            <Timeline style={{paddingTop: 8}}>
                                {groupByCorrelation(items).map((group) => (
                                    <HistoryGroup key={group[0].correlationId ?? group[0].id} items={group} />
                                ))}
                            </Timeline>
                        </div>
                    ))}
                </>
            )}

            {/* Einträge der letzten Wochen */}
            {Object.keys(groupedItems.weeks).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Letzte Wochen"}
                    </Divider>
                    {Object.entries(groupedItems.weeks).map(([week, items]) => (
                        <div key={week} className={css.timelineGroup}>
                            <div className={css.timelineGroupHeader}>{week}</div>
                            <Timeline style={{paddingTop: 8}}>
                                {groupByCorrelation(items).map((group) => (
                                    <HistoryGroup key={group[0].correlationId ?? group[0].id} items={group} />
                                ))}
                            </Timeline>
                        </div>
                    ))}
                </>
            )}

            {/* Einträge der letzten Monate */}
            {Object.keys(groupedItems.months).length > 0 && (
                <>
                    <Divider orientation="left" className={css.periodDivider} orientationMargin={0}>
                        {"Ältere Einträge"}
                    </Divider>
                    {Object.entries(groupedItems.months).map(([month, items]) => (
                        <div key={month} className={css.timelineGroup}>
                            <div className={css.timelineGroupHeader}>{month}</div>
                            <Timeline style={{paddingTop: 8}}>
                                {groupByCorrelation(items).map((group) => (
                                    <HistoryGroup key={group[0].correlationId ?? group[0].id} items={group} />
                                ))}
                            </Timeline>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default BookingHistoryContent;
